import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import {
  type BoardDetail,
  type Card,
  CardCover,
  cardsInColumn,
  DeadlineBadge,
  displayColumnName,
  isSingleUrl,
  LabelChip,
  normalizeCardTarget,
  useCopyCard,
  useDeleteCard,
  useMoveCard,
  useRefreshLinkPreview,
  useSetCardArchived,
  useSetCardCompleted,
  useSetCardPinned,
  useUndoTrash,
} from '@/entities/board';
import { type CardDetail, useCard } from '@/entities/card';
import { taskAbilities, useParticipants } from '@/entities/collab';
import { usePresence, useReportEditing } from '@/entities/realtime';
import { useViewer } from '@/entities/viewer';
import { MoveCardToBoardDialog } from '@/features/board-move';
import { CardHistory } from '@/features/card-history';
import { FormError } from '@/shared/forms';
import { useCopyLink } from '@/features/resource-nav';
import { SaveTemplateDialog } from '@/features/template-manage';
import {
  Button,
  Combobox,
  ConfirmDialog,
  Dialog,
  ErrorState,
  HelpTip,
  IconButton,
  Icons,
  LoadingState,
  MenuItem,
  MenuList,
  MenuRow,
  Popover,
  useToast,
} from '@/shared/ui';

import { AttachmentsSection } from './AttachmentsSection';
import { CardAppearanceDialog } from './CardAppearanceDialog';
import styles from './CardDialog.module.css';
import { CoverEditor } from './CoverEditor';
import { ContentBlocks } from './ContentBlocks';
import { DeadlineEditor } from './DeadlineEditor';
import { LabelPicker } from './LabelPicker';
import { RecurrenceSection } from './RecurrenceSection';
import { LinkPreviewSummary } from './LinkPreviewSummary';
import { returnStateFrom } from '@/shared/lib/return-target';

import { CardAbilitiesContext, CardEditingContext } from './abilities';
import { AssigneeEditor } from './AssigneeEditor';
import { CommentsPanel } from './CommentsPanel';
import collabStyles from './Collab.module.css';
import { SaveIndicator } from './SaveIndicator';
import { WatchersControl } from './WatchersControl';
import { TitleEditor } from './TitleEditor';

interface CardDialogProps {
  cardId: string;
  board: BoardDetail;
  onClose: () => void;
}

/**
 * Card popup. Structured as sections (title, labels, content) and a sidebar of
 * actions, so later phases add deadline, history, comments... as new sections
 * without reshaping the dialog.
 */
export function CardDialog({ cardId, board, onClose }: CardDialogProps) {
  const { t } = useTranslation();
  const card = useCard(cardId);

  if (card.isPending) {
    // The board already holds the card's summary: show it at once (read-only) and let the
    // heavy parts (content, history, attachments, recurrence) arrive without a blank dialog.
    const summary = board.cards.find((c) => c.id === cardId);
    if (summary) return <CardDialogShell summary={summary} board={board} onClose={onClose} />;
    return (
      <Dialog title={t('cards.loading')} onClose={onClose} size="lg">
        <LoadingState />
      </Dialog>
    );
  }
  if (card.isError) {
    return (
      <Dialog title={t('cards.notAvailable')} onClose={onClose} size="lg">
        <ErrorState error={card.error} />
      </Dialog>
    );
  }
  return <CardDialogContent card={card.data} board={board} onClose={onClose} />;
}

/** Summary-only dialog: nothing here is editable, so an unloaded field is never saved as empty. */
function CardDialogShell({
  summary,
  board,
  onClose,
}: {
  summary: Card;
  board: BoardDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const column = board.columns.find((c) => c.id === summary.placement.column_id);
  const labels = board.labels.filter((label) => summary.label_ids.includes(label.id));
  return (
    <Dialog title={summary.title} onClose={onClose} size="lg" hideTitle>
      {summary.cover && (
        <CardCover cover={summary.cover} className={styles.cover} testId="dialog-cover" />
      )}
      <div className={styles.head} data-testid="card-head-shell" aria-busy="true">
        <h2 className={styles.shellTitle}>{summary.title}</h2>
        {column && (
          <p className={styles.meta}>
            {t('cards.inColumn', {
              column: displayColumnName(column, t('columns.calendar')),
            })}
          </p>
        )}
        <div className={styles.shellChips}>
          {labels.map((label) => (
            <LabelChip key={label.id} label={label} emptyName={t('labels.unnamed')} />
          ))}
          <DeadlineBadge card={summary} />
        </div>
      </div>
      <LoadingState />
    </Dialog>
  );
}

function CardDialogContent({
  card,
  board,
  onClose,
}: {
  card: CardDetail;
  board: BoardDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const boardId = board.board.id;
  const setCompleted = useSetCardCompleted(boardId);
  const setCardArchived = useSetCardArchived(boardId);
  const setPinned = useSetCardPinned(boardId);
  const moveCard = useMoveCard(boardId);
  const deleteCard = useDeleteCard(boardId);
  const copyCard = useCopyCard(boardId);
  const undoTrash = useUndoTrash(boardId);
  const toast = useToast();
  const copyLink = useCopyLink();
  const [savingTemplate, setSavingTemplate] = useState(false);
  const refreshPreview = useRefreshLinkPreview();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editor, setEditor] = useState<'cover' | 'look' | 'board' | null>(null);
  const [tab, setTab] = useState<'details' | 'history' | 'comments'>('details');
  // The board copy is updated optimistically: prefer it for placement and assignment state.
  const preview = board.cards.find((c) => c.id === card.id);
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const abilities = useMemo(
    () => taskAbilities(board.permissions, preview ?? card, viewerId),
    [board.permissions, preview, card, viewerId],
  );
  // Which editors are open right now (reported so that others see "X is editing").
  const openEditors = useRef(new Set<string>());
  const [editingNow, setEditingNow] = useState(false);
  const signalEditing = useCallback((source: string, active: boolean) => {
    if (active) openEditors.current.add(source);
    else openEditors.current.delete(source);
    setEditingNow(openEditors.current.size > 0);
  }, []);
  useReportEditing(boardId, editingNow ? card.id : null);
  const presence = usePresence(boardId);
  const participants = useParticipants(boardId);
  const otherEditors = (presence.editing[card.id] ?? [])
    .filter((id) => id !== viewerId)
    .map((id) => participants.data?.find((p) => p.user_id === id)?.display_name ?? '…');
  const placement = preview?.placement ?? card.placement;
  const completed = (preview?.completed_at ?? card.completed_at) !== null;
  const column = board.columns.find((c) => c.id === placement.column_id);
  // Same rule as the tile: preview only while the whole title is still one link.
  const linkPreview = card.link_preview && isSingleUrl(card.title) ? card.link_preview : null;

  const moveTo = (columnId: string, position: 'top' | 'bottom') => {
    // "Top" = start of the card's own section (pinned cards stay among pinned).
    const first = cardsInColumn(board, columnId).find(
      (c) => c.id !== card.id && c.placement.is_pinned === placement.is_pinned,
    );
    moveCard.mutate({
      cardId: card.id,
      target: normalizeCardTarget(
        board,
        card.id,
        position === 'top' && first
          ? { columnId, anchorCardId: first.id, edge: 'before' }
          : { columnId, anchorCardId: null },
      ),
    });
  };

  if (confirmDelete) {
    return (
      <ConfirmDialog
        title={t('cards.deleteTitle')}
        message={t('cards.deleteMessage', { title: card.title })}
        confirmLabel={t('common.delete')}
        pending={deleteCard.isPending}
        onCancel={() => {
          setConfirmDelete(false);
        }}
        onConfirm={() => {
          deleteCard.mutate(card.id, {
            onSuccess: () => {
              onClose();
              toast.show({
                message: t('toast.cardTrashed'),
                actionLabel: t('toast.undo'),
                onAction: () => undoTrash.mutateAsync(card.id),
              });
            },
          });
        }}
      />
    );
  }

  const closeAnd = (close: () => void, action: () => void) => () => {
    close();
    action();
  };

  return (
    <CardAbilitiesContext.Provider value={abilities}>
      <CardEditingContext.Provider value={signalEditing}>
        <Dialog title={card.title} onClose={onClose} size="xl" hideTitle>
          {card.cover && (
            <CardCover cover={card.cover} className={styles.cover} testId="dialog-cover" />
          )}
          <div className={styles.editorHead} data-testid="card-head">
            <div className={styles.titleLine}>
              <div className={styles.titleBox}>
                <TitleEditor card={card} />
              </div>
              <div className={styles.headActions}>
                <SaveIndicator />
                <IconButton
                  label={completed ? t('cardEditor.markOpen') : t('cardEditor.markDone')}
                  icon={<Icons.check />}
                  pressed={completed}
                  disabled={setCompleted.isPending || !abilities.editContent}
                  className={completed ? styles.doneOn : undefined}
                  onClick={(event) => {
                    setCompleted.mutate({
                      cardId: card.id,
                      completed: !completed,
                      anchor: event.currentTarget,
                    });
                  }}
                />
                <IconButton
                  label={placement.is_pinned ? t('cardEditor.pinned') : t('cardEditor.notPinned')}
                  icon={<Icons.pin />}
                  pressed={placement.is_pinned}
                  disabled={setPinned.isPending || !abilities.move}
                  onClick={() => {
                    setPinned.mutate({ cardId: card.id, pinned: !placement.is_pinned });
                  }}
                />
                <Popover
                  label={t('cardEditor.more')}
                  width={300}
                  alignEnd
                  trigger={(props) => (
                    <IconButton label={t('cardEditor.more')} icon={<Icons.more />} {...props} />
                  )}
                >
                  {(close) => (
                    <MenuList>
                      <MenuRow>
                        <MenuItem
                          icon={<Icons.link />}
                          data-testid="card-copy-link"
                          onClick={closeAnd(close, () => {
                            copyLink(`/app/boards/${boardId}/cards/${card.id}`);
                          })}
                        >
                          {t('links.copyTask')}
                        </MenuItem>
                      </MenuRow>
                      <MenuRow>
                        <MenuItem
                          icon={<Icons.copy />}
                          disabled={copyCard.isPending || !abilities.move}
                          onClick={closeAnd(close, () => {
                            copyCard.mutate(card.id, {
                              onSuccess: () => {
                                onClose();
                                toast.show({ message: t('toast.cardCopied') });
                              },
                            });
                          })}
                        >
                          {t('cards.copy')}
                        </MenuItem>
                      </MenuRow>
                      <MenuRow>
                        <MenuItem
                          icon={<Icons.layers />}
                          data-testid="card-save-template"
                          onClick={closeAnd(close, () => {
                            setSavingTemplate(true);
                          })}
                        >
                          {t('templates.action')}
                        </MenuItem>
                      </MenuRow>
                      <MenuRow>
                        <MenuItem
                          icon={<Icons.move />}
                          disabled={!abilities.move}
                          onClick={closeAnd(close, () => {
                            setEditor('board');
                          })}
                        >
                          {t('move.toBoard')}
                        </MenuItem>
                      </MenuRow>
                      <MenuRow>
                        <MenuItem
                          icon={<Icons.archive />}
                          disabled={!abilities.editContent}
                          onClick={closeAnd(close, () => {
                            setCardArchived.mutate(
                              { cardId: card.id, archived: true },
                              { onSuccess: onClose },
                            );
                          })}
                        >
                          {t('cardEditor.archive')}
                        </MenuItem>
                      </MenuRow>
                      <MenuRow>
                        <MenuItem
                          icon={<Icons.image />}
                          disabled={!abilities.editContent}
                          onClick={closeAnd(close, () => {
                            setEditor('cover');
                          })}
                        >
                          {card.cover ? t('cover.change') : t('cover.add')}
                        </MenuItem>
                      </MenuRow>
                      {linkPreview && (
                        <MenuRow>
                          <MenuItem
                            icon={<Icons.refresh />}
                            disabled={refreshPreview.isPending || linkPreview.status === 'pending'}
                            onClick={closeAnd(close, () => {
                              refreshPreview.mutate(card);
                            })}
                          >
                            {t('cards.refreshPreview')}
                          </MenuItem>
                        </MenuRow>
                      )}
                      <MenuRow>
                        <MenuItem
                          icon={<Icons.settings />}
                          onClick={closeAnd(close, () => {
                            // Settings remember this card: "Return to the card" brings it back.
                            void navigate('/app/settings/interface', {
                              state: returnStateFrom(location),
                            });
                          })}
                        >
                          {t('cardEditor.settings')}
                        </MenuItem>
                      </MenuRow>
                      <MenuRow separated>
                        <MenuItem
                          danger
                          icon={<Icons.trash />}
                          disabled={!abilities.remove}
                          onClick={closeAnd(close, () => {
                            setConfirmDelete(true);
                          })}
                        >
                          {t('cards.delete')}
                        </MenuItem>
                      </MenuRow>
                    </MenuList>
                  )}
                </Popover>
              </div>
            </div>
            {column && (
              <p
                className={styles.meta}
                title={t('cards.inColumn', {
                  column: displayColumnName(column, t('columns.calendar')),
                })}
              >
                {t('cards.inColumn', { column: displayColumnName(column, t('columns.calendar')) })}
              </p>
            )}
            {linkPreview && <LinkPreviewSummary preview={linkPreview} />}
            {(copyCard.error ?? refreshPreview.error ?? setCardArchived.error) && (
              <FormError error={copyCard.error ?? refreshPreview.error ?? setCardArchived.error} />
            )}
          </div>

          <div className={styles.editorLayout}>
            <div className={styles.content}>
              <div className={styles.tabs} role="tablist" aria-label={t('cards.sections')}>
                {(
                  [
                    'details',
                    ...(abilities.viewHistory ? ['history' as const] : []),
                    'comments',
                  ] as const
                ).map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    className={`${styles.tab} ${tab === value ? styles.tabOn : ''}`}
                    aria-selected={tab === value}
                    onClick={() => {
                      setTab(value);
                    }}
                  >
                    {t(
                      value === 'details'
                        ? 'history.details'
                        : value === 'history'
                          ? 'history.tab'
                          : 'comments.tab',
                    )}
                  </button>
                ))}
              </div>
              {otherEditors.length > 0 && (
                <p className={collabStyles.banner} role="status" data-testid="editing-banner">
                  {otherEditors.length === 1
                    ? t('presence.editingOne', { name: otherEditors[0] })
                    : t('presence.editingMany', { names: otherEditors.join(', ') })}
                </p>
              )}
              {tab === 'details' ? (
                <>
                  <ContentBlocks card={card} />
                  <AttachmentsSection card={card} boardId={boardId} />
                </>
              ) : tab === 'comments' ? (
                <CommentsPanel cardId={card.id} />
              ) : (
                <CardHistory
                  cardId={card.id}
                  boardId={board.board.id}
                  cardVersion={card.version}
                  onRestored={() => {
                    setTab('details');
                  }}
                />
              )}
            </div>

            <aside className={styles.properties} aria-label={t('cardEditor.properties')}>
              <AssigneeEditor card={preview ?? card} boardId={boardId} />
              <WatchersControl cardId={card.id} />
              {abilities.move && (
                <MoveControls
                  board={board}
                  currentColumnId={placement.column_id}
                  pending={moveCard.isPending}
                  onMove={moveTo}
                />
              )}
              <div inert={!abilities.manageLabels ? true : undefined}>
                <LabelPicker card={card} board={board} />
              </div>
              <div inert={!abilities.editSchedule ? true : undefined}>
                <DeadlineEditor card={card} />
              </div>
              <RecurrenceSection
                card={card}
                canEdit={abilities.editSchedule}
                onDeleteThis={
                  abilities.remove
                    ? () => {
                        setConfirmDelete(true);
                      }
                    : undefined
                }
                onBulkDeleted={onClose}
              />
              <div className={styles.propertyRow}>
                <button
                  type="button"
                  className={styles.propertyButton}
                  disabled={!abilities.editContent}
                  onClick={() => {
                    setEditor('look');
                  }}
                >
                  <span className={styles.propertyName}>{t('cardLook.open')}</span>
                  <span className={styles.propertyChevron} aria-hidden>
                    <Icons.chevronRight />
                  </span>
                </button>
                <HelpTip topic={t('cardLook.open')} topicId="kanban" text={t('help.appearance')} />
              </div>
            </aside>
          </div>
          {editor === 'cover' && (
            <CoverEditor
              card={card}
              boardId={boardId}
              onClose={() => {
                setEditor(null);
              }}
            />
          )}
          {editor === 'look' && (
            <CardAppearanceDialog
              card={card}
              column={column}
              onClose={() => {
                setEditor(null);
              }}
            />
          )}
          {editor === 'board' && (
            <MoveCardToBoardDialog
              card={preview ?? card}
              source={board}
              onClose={() => {
                setEditor(null);
              }}
              onMoved={onClose}
            />
          )}
          {savingTemplate && (
            <SaveTemplateDialog
              kind="task"
              sourceId={card.id}
              defaultName={card.title}
              onClose={() => {
                setSavingTemplate(false);
              }}
            />
          )}
        </Dialog>
      </CardEditingContext.Provider>
    </CardAbilitiesContext.Provider>
  );
}

/** Non-drag way to move a card (keyboard, touch, screen readers). */
function MoveControls({
  board,
  currentColumnId,
  pending,
  onMove,
}: {
  board: BoardDetail;
  currentColumnId: string;
  pending: boolean;
  onMove: (columnId: string, position: 'top' | 'bottom') => void;
}) {
  const { t } = useTranslation();
  const [columnId, setColumnId] = useState(currentColumnId);
  return (
    <div className={styles.moveControls}>
      <div className={styles.moveLabel}>
        <span>{t('cards.column')}</span>
        <Combobox
          label={t('cards.column')}
          value={columnId}
          options={board.columns.map((c) => ({
            value: c.id,
            label: displayColumnName(c, t('columns.calendar')),
          }))}
          onChange={setColumnId}
        />
      </div>
      <div className={styles.moveButtons}>
        <Button
          size="sm"
          disabled={pending}
          onClick={() => {
            onMove(columnId, 'top');
          }}
        >
          {t('cards.moveTop')}
        </Button>
        <Button
          size="sm"
          disabled={pending}
          onClick={() => {
            onMove(columnId, 'bottom');
          }}
        >
          {t('cards.moveBottom')}
        </Button>
      </div>
    </div>
  );
}
